import React from "react";
import { MarkerType, Position } from "react-flow-renderer";
import { Intent } from "@blueprintjs/core";
import { lowerFirst, uniq } from "lodash";
import { getNumberFromNumberString, renderNumberValue } from "utils/string";
import { isNotEmpty } from "utils/validate";
import { accuracyLevels, cellModelParameterUnitsType, dynamicRateTimeTypes, findDictionaryItemByValue, getDefaultUnitsPrefix, lPerCellUnitsType, recipeParameterUnitsType, SUPERSCRIPT_STYLES, } from "shared/rsDictionary";
import Toaster, { toastIcons } from "components/Page/components/Toaster";
import HtmlText from "components/HtmlText";
import AccuracyIcon from "rs-components/AccuracyIndicator/components/AccuracyIcon";
import { DependencyVariablesByType } from "pages/CellModel/components/CellModelCanvas/components/DependencyVariableDialog";
import { AccuracyLevel, DependencyType, DynamicType, EdgeDirection, NodeType, RefinementRunState, } from "./cell-model.types";
import { Accuracy, OptimizationParameterParameterType, } from "store/rs-workspace/rs-workspace.types";
import { NoUnitsType } from "../recipe/recipe.types";
import { GlobalEntityType } from "shared/types";
import RefinedCircleIcon from "icons/refined-circle.svg";
export const REFINEMENT_STARTED_LOCAL_STORAGE_ITEM = "refinementStarted";
const DEFAULT_COLOR = "#f06ea5";
const DEFAULT_GREY = "#9d9d9d"; // $s500
const HIGHLIGHT_COLOR = "#252525";
export const MARGIN = 8;
export const VARIABLE_NODE_WIDTH = 270;
export const VARIABLE_NODE_HEIGHT = 48;
export const VARIABLE_TOP_MARGIN = MARGIN;
export const VARIABLE_BOTTOM_MIN_MARGIN = MARGIN;
export const DYNAMIC_TOP_MARGIN = 12;
export const DYNAMICS_NODE_HEIGHT = 48;
export const DYNAMIC_BOTTOM_MARGIN = DYNAMIC_TOP_MARGIN;
export const MAX_POPULATIONS = 10;
export const POPULATION_MIN_HEIGHT = 96;
export const POPULATION_BOTTOM_MARGIN = MARGIN;
export const ADD_BUTTON_HEIGHT = 32;
export const ADD_DYNAMIC_WITH_MARGIN_HEIGHT = 26;
const DYNAMIC_LABEL = {
    [DynamicType.FIXED]: `${DynamicType.FIXED} at`,
    [DynamicType.RANGE]: DynamicType.RANGE,
    [DynamicType.DISTRIBUTION]: DynamicType.DISTRIBUTION,
};
const getElementColor = ({ color, highlightColor }, isCompleted, isHighlight, shouldHighLightItem) => {
    if (isHighlight) {
        return shouldHighLightItem ? highlightColor || color : DEFAULT_GREY;
    }
    else {
        return isCompleted ? color : DEFAULT_GREY;
    }
};
export const refineNode = ({ id, name, internalType, position, originalId, populationId, color = "", highlightColor = "", isNoDrivingPopulation, canHandleDynamicEffect, isCompleted, }, widgetWidth) => ({
    id,
    type: internalType,
    data: {
        id,
        name,
        internalType,
        color,
        highlightColor,
        widgetWidth,
        originalId,
        populationId,
        isNoDrivingPopulation,
        canHandleDynamicEffect,
        isCompleted,
    },
    position: position,
    draggable: false,
    style: { zIndex: 3 },
    sourcePosition: Position.Left,
    targetPosition: internalType === NodeType.VARIABLE ? Position.Left : Position.Right,
});
const prepareDynamicLabel = (data, units = "", refinement, currentRefinementTab) => {
    if (!isUncertaintyPropertiesConfigured(data)) {
        return;
    }
    const refinedValue = getRefineResultByParameterId(data?.parameterId)(refinement, currentRefinementTab);
    if (refinedValue) {
        const value = renderNumberValue(refinedValue.data?.mostLikely);
        return { label: `Most likely: ${value} ${units}`, refined: true };
    }
    let label;
    switch (data?.type) {
        case DynamicType.RANGE:
            label = `${DYNAMIC_LABEL[data.type]} (Min: ${renderNumberValue(data.from)} ${units}, Max: ${renderNumberValue(data.to)} ${units})`;
            break;
        case DynamicType.FIXED:
            label = `${DYNAMIC_LABEL[data.type]} ${renderNumberValue(data.value)} ${units}`;
            break;
        case DynamicType.DISTRIBUTION:
            label = `${DYNAMIC_LABEL[data.type]} (Center: ${renderNumberValue(data.mean)} ${units}, SD: ${renderNumberValue(data.sd)})`;
            break;
    }
    return isNotEmpty(label) ? { label: label } : undefined;
};
const prepareVariableEffectLabel = (data, units = "", refinement, currentRefinementTab) => {
    if (!isUncertaintyPropertiesConfigured(data)) {
        return;
    }
    const unitsLabel = React.createElement(HtmlText, { text: findDictionaryItemByValue(cellModelParameterUnitsType, units)?.label });
    const suffix = (React.createElement("span", null,
        React.createElement("span", null, "* 10"),
        React.createElement("span", { style: SUPERSCRIPT_STYLES }, "-6")));
    const refinedValue = getRefineResultByParameterId(data?.parameterId)(refinement, currentRefinementTab);
    if (refinedValue) {
        return {
            nodeText: (React.createElement(React.Fragment, null,
                "Most likely: ",
                renderNumberValue(refinedValue.data?.mostLikely),
                " ",
                suffix,
                " ",
                unitsLabel)),
            icon: React.createElement(RefinedCircleIcon, null),
        };
    }
    let nodeText;
    switch (data?.type) {
        case DynamicType.RANGE:
            nodeText = (React.createElement(React.Fragment, null,
                DYNAMIC_LABEL[data.type],
                " (Min: ",
                renderNumberValue(data.from),
                " ",
                suffix,
                " ",
                unitsLabel,
                ", Max:",
                " ",
                renderNumberValue(data.to),
                " ",
                suffix,
                " ",
                unitsLabel,
                ")"));
            break;
        case DynamicType.FIXED:
            nodeText = (React.createElement(React.Fragment, null,
                DYNAMIC_LABEL[data.type],
                " ",
                renderNumberValue(data.value),
                " ",
                suffix,
                " ",
                unitsLabel));
            break;
        case DynamicType.DISTRIBUTION:
            nodeText = (React.createElement(React.Fragment, null,
                DYNAMIC_LABEL[data.type],
                " (Center: ",
                renderNumberValue(data.mean),
                " ",
                suffix,
                " ",
                unitsLabel,
                ", SD:",
                " ",
                renderNumberValue(data.sd),
                ")"));
            break;
    }
    return isNotEmpty(nodeText) ? { nodeText } : undefined;
};
const prepareDependencyLabel = (data, units = "", refinement, currentRefinementTab) => {
    const propertiesToDisplay = [];
    switch (data?.type) {
        case DependencyType.Range:
            propertiesToDisplay.push({ value: data.from, prefix: DependencyVariablesByType.Range[0].prefix }, { value: data.to, prefix: DependencyVariablesByType.Range[1].prefix });
            break;
        case DependencyType.OutOfRange:
            propertiesToDisplay.push({ value: data.from, prefix: DependencyVariablesByType.OutOfRange[0].prefix }, { value: data.to, prefix: DependencyVariablesByType.OutOfRange[1].prefix });
            break;
        case DependencyType.Distribution:
            propertiesToDisplay.push({ value: data.mean, prefix: DependencyVariablesByType.Distribution[0].prefix }, { value: data.sd, prefix: DependencyVariablesByType.Distribution[1].prefix });
            break;
        case DependencyType.AsymmetricalDistribution:
            propertiesToDisplay.push({ value: data.mean, prefix: DependencyVariablesByType.AsymmetricalDistribution[0].prefix }, { value: data.leftSD, prefix: DependencyVariablesByType.AsymmetricalDistribution[1].prefix }, { value: data.rightSD, prefix: DependencyVariablesByType.AsymmetricalDistribution[2].prefix });
            break;
        case DependencyType.NegativeAsymmetricalDistribution:
            propertiesToDisplay.push({ value: data.mean, prefix: DependencyVariablesByType.NegativeAsymmetricalDistribution[0].prefix }, { value: data.leftSD, prefix: DependencyVariablesByType.NegativeAsymmetricalDistribution[1].prefix }, { value: data.rightSD, prefix: DependencyVariablesByType.NegativeAsymmetricalDistribution[2].prefix });
            break;
        case DependencyType.NegativeDistribution:
            propertiesToDisplay.push({ value: data.mean, prefix: DependencyVariablesByType.NegativeDistribution[0].prefix }, { value: data.sd, prefix: DependencyVariablesByType.NegativeDistribution[1].prefix });
            break;
        case DependencyType.Saturating:
            propertiesToDisplay.push({
                value: data.halfEffect,
                prefix: DependencyVariablesByType.Saturating[0].prefix,
            });
            break;
        case DependencyType.NegativeSaturating:
            propertiesToDisplay.push({
                value: data.halfEffect,
                prefix: DependencyVariablesByType.NegativeSaturating[0].prefix,
            });
            break;
        case DependencyType.Sigmoidal:
            propertiesToDisplay.push({ value: data.percent50, prefix: DependencyVariablesByType.Sigmoidal[0].prefix }, { value: data.percent95, prefix: DependencyVariablesByType.Sigmoidal[1].prefix });
            break;
        case DependencyType.NegativeSigmoidal:
            propertiesToDisplay.push({ value: data.percent50, prefix: DependencyVariablesByType.NegativeSigmoidal[0].prefix }, { value: data.percent05, prefix: DependencyVariablesByType.NegativeSigmoidal[1].prefix });
            break;
    }
    const mappedProperties = propertiesToDisplay
        .map(({ value, prefix }) => ({
        label: prepareDynamicLabel(value, units, refinement, currentRefinementTab),
        prefix,
    }))
        .filter(({ label }) => isNotEmpty(label));
    if (!mappedProperties.length) {
        return;
    }
    return {
        nodeText: mappedProperties.map(({ label, prefix }) => `${prefix} ${lowerFirst(label?.label)}`).join(", "),
        icon: mappedProperties.some(({ label }) => label?.refined) ? React.createElement(RefinedCircleIcon, null) : undefined,
    };
};
export const preparePopulationEffects = (populations = [], nodes, highlightedNode, effectsOnVariable, dynamicDependencies, refinement, currentRefinementTab) => populations
    .map(({ dynamicEffects = [], id: populationId }) => dynamicEffects.reduce((acc, { id, dynamicId, uncertaintyProperties, isCompleted }) => {
    const source = `d${dynamicId}`;
    const target = `p${populationId}`;
    const sourceData = nodes.find(({ id }) => id === source);
    const rateTime = dynamicRateTimeTypes.find(({ value }) => value === sourceData?.rateTimeType)?.label;
    const units = rateTime ? `${lPerCellUnitsType.label} ${rateTime}` : undefined;
    const isUncertainty = isUncertaintyPropertiesConfigured(uncertaintyProperties);
    const labelColor = (isUncertainty && sourceData?.color) || DEFAULT_GREY;
    const { isHighlight, node } = highlightedNode;
    let shouldHighLightItem = false;
    if (node?.type === NodeType.DYNAMIC) {
        shouldHighLightItem = dynamicId === node?.id;
    }
    if (node?.type === NodeType.VARIABLE) {
        const variableSource = `v${node.id}`;
        const depsIds = dynamicDependencies
            .filter((item) => item.source === variableSource)
            .map((item) => item.data.dynamicId);
        const effectsIds = effectsOnVariable
            .filter((item) => item.target === variableSource)
            .map((item) => item.data.dynamicId);
        const ids = [...depsIds, ...effectsIds];
        shouldHighLightItem = ids.includes(dynamicId);
    }
    const label = prepareDynamicLabel(uncertaintyProperties, units, refinement, currentRefinementTab);
    return [
        ...acc,
        {
            id: `${source}-${target}`,
            type: "edge",
            source,
            target,
            sourceHandle: `pd${dynamicId}`,
            arrowHeadType: MarkerType.ArrowClosed,
            data: {
                direction: EdgeDirection.Left,
                originalId: id,
                populationId,
                isEffect: true,
                label: label
                    ? { nodeText: label?.label, icon: label?.refined ? React.createElement(RefinedCircleIcon, null) : undefined }
                    : undefined,
                color: getElementColor({ color: labelColor, highlightColor: HIGHLIGHT_COLOR }, isCompleted, highlightedNode.isHighlight, shouldHighLightItem),
                isCompleted,
                dynamicId,
                shouldHighLightEdge: shouldHighLightItem,
                isHighlightMode: isHighlight,
            },
        },
    ];
}, []))
    .flat();
const shouldHighlightDynamicDependencyOrEffect = (highlightedNode, variablesNodes, id, variableId) => {
    switch (highlightedNode.node?.type) {
        case NodeType.DYNAMIC:
            return id === highlightedNode.node?.id;
        case NodeType.VARIABLE: {
            const hasVariableNodes = variablesNodes.some((item) => item.originalId === highlightedNode.node?.id);
            return highlightedNode.node?.id == variableId && hasVariableNodes;
        }
        default:
            return false;
    }
};
const prepareDynamicNodes = (dynamics, variableNodes, populationId, color, yPosition, highlightedNode, refinement, currentRefinementTab) => {
    const nodes = [];
    let effects = [];
    let deps = [];
    let dynamicBottomYPos = 0;
    dynamics.forEach(({ id, name, dependencies, effectOnVariables, rateTimeType, isCompleted }, index) => {
        const preparedDynamicNodesByPopulation = nodes.filter((item) => item.populationId == populationId);
        const { bottomYPos } = { ...preparedDynamicNodesByPopulation[index - 1] };
        const { isHighlight, node } = highlightedNode;
        let shouldHighLightItem = false;
        if (node?.type === NodeType.DYNAMIC) {
            shouldHighLightItem = id === highlightedNode.node?.id;
        }
        if (node?.type === NodeType.VARIABLE) {
            const hasDependency = dependencies.some((item) => item.variableId === node?.id);
            const hasEffect = effectOnVariables.some((item) => item.variableId === node?.id);
            shouldHighLightItem = hasDependency || hasEffect;
        }
        dynamicBottomYPos =
            bottomYPos !== undefined
                ? (bottomYPos || 0) + DYNAMICS_NODE_HEIGHT + DYNAMIC_BOTTOM_MARGIN
                : yPosition + DYNAMIC_TOP_MARGIN;
        nodes.push({
            id: `d${id}`,
            originalId: id,
            populationId,
            name: name,
            internalType: NodeType.DYNAMIC,
            bottomYPos: dynamicBottomYPos,
            position: {
                x: 352,
                y: dynamicBottomYPos,
            },
            color: getElementColor({ color }, isCompleted, isHighlight, shouldHighLightItem),
            highlightColor: shouldHighLightItem ? HIGHLIGHT_COLOR : undefined,
            rateTimeType,
            isCompleted,
        });
        effects = [
            ...effects,
            ...effectOnVariables.map(({ id: effectId, variableId, uncertaintyProperties, isCompleted: effectIsCompleted }) => {
                const units = variableNodes.find(({ originalId }) => originalId === variableId)?.unit;
                const isUncertainty = isUncertaintyPropertiesConfigured(uncertaintyProperties);
                const labelColor = (isUncertainty && color) || DEFAULT_GREY;
                const shouldHighLightItem = shouldHighlightDynamicDependencyOrEffect(highlightedNode, variableNodes, id, variableId);
                return {
                    id: `d${id}-v${variableId}`,
                    type: "edge",
                    source: `d${id}`,
                    target: `v${variableId}`,
                    sourceHandle: `vd${id}`,
                    arrowHeadType: MarkerType.ArrowClosed,
                    data: {
                        direction: EdgeDirection.Right,
                        originalId: effectId,
                        variableId,
                        isEffect: true,
                        isCompleted: effectIsCompleted,
                        color: getElementColor({ color: labelColor, highlightColor: HIGHLIGHT_COLOR }, isCompleted || effectIsCompleted, highlightedNode.isHighlight, shouldHighLightItem),
                        dynamicId: id,
                        label: prepareVariableEffectLabel(uncertaintyProperties, units, refinement, currentRefinementTab),
                        shouldHighLightEdge: shouldHighLightItem,
                        isHighlightMode: isHighlight,
                    },
                };
            }),
        ];
        deps = [
            ...deps,
            ...dependencies.map(({ id: depId, variableId, dependencyProperties, isCompleted: dependencyIsCompleted }) => {
                const variableNode = variableNodes.find(({ originalId }) => originalId === variableId);
                const variableNodeUnits = variableNode?.unit && variableNode.unit !== NoUnitsType.NoUnits
                    ? findDictionaryItemByValue(cellModelParameterUnitsType, variableNode.unit, getDefaultUnitsPrefix)?.label
                    : undefined;
                const label = prepareDependencyLabel(dependencyProperties, variableNodeUnits, refinement, currentRefinementTab);
                const labelColor = (label && variableNode?.color) ?? DEFAULT_GREY;
                const shouldHighLightItem = shouldHighlightDynamicDependencyOrEffect(highlightedNode, variableNodes, id, variableId);
                return {
                    id: `v${variableId}-d${id}`,
                    type: "edge",
                    source: `v${variableId}`,
                    target: `d${id}`,
                    arrowHeadType: MarkerType.ArrowClosed,
                    data: {
                        direction: EdgeDirection.Left,
                        originalId: depId,
                        variableId,
                        isDependency: true,
                        isEffect: false,
                        dynamicId: id,
                        label,
                        isCompleted: dependencyIsCompleted,
                        color: getElementColor({ color: labelColor, highlightColor: HIGHLIGHT_COLOR }, isCompleted || dependencyIsCompleted, highlightedNode.isHighlight, shouldHighLightItem),
                        shouldHighLightEdge: shouldHighLightItem,
                        isHighlightMode: isHighlight,
                    },
                };
            }),
        ];
    });
    return {
        dynamicNodesByPopulation: nodes,
        dynamicEffectsByPopulation: effects,
        dynamicDependenciesByPopulation: deps,
    };
};
export const getGeneralRelatedDynamics = (populations, variableId) => {
    let ids = [];
    for (const population of populations) {
        const { dynamics } = population;
        for (const dynamic of dynamics) {
            const depsIds = dynamic.dependencies.some((item) => item.variableId === variableId);
            const effectsIds = dynamic.effectOnVariables.some((item) => item.variableId === variableId);
            if (depsIds || effectsIds) {
                ids = [...ids, dynamic.id];
            }
        }
    }
    return ids;
};
export const preparePopulationNodes = (populations, variableNodes, highlightedNode, refinement, currentRefinementTab) => {
    const populationNodes = [];
    let dynamicNodes = [];
    let effectsOnVariable = [];
    let dynamicDependencies = [];
    let populationBottomYPos = 0;
    const ids = highlightedNode.node?.id
        ? getGeneralRelatedDynamics(populations, highlightedNode.node?.id)
        : [];
    populations.forEach(({ id, name, dynamics, dynamicEffects, color, isNoDriving, isCompleted }, index) => {
        const populationHeight = Math.max(POPULATION_MIN_HEIGHT + ADD_DYNAMIC_WITH_MARGIN_HEIGHT, DYNAMIC_TOP_MARGIN +
            dynamics.length * (DYNAMICS_NODE_HEIGHT + DYNAMIC_BOTTOM_MARGIN) +
            ADD_DYNAMIC_WITH_MARGIN_HEIGHT);
        const previousPopulation = populationNodes[index - 1];
        populationBottomYPos =
            previousPopulation !== undefined
                ? (previousPopulation?.bottomYPos || 0) + populationHeight + POPULATION_BOTTOM_MARGIN
                : populationHeight + POPULATION_BOTTOM_MARGIN;
        const y = previousPopulation !== undefined
            ? (previousPopulation?.bottomYPos || 0) + POPULATION_BOTTOM_MARGIN
            : POPULATION_BOTTOM_MARGIN;
        const { isHighlight, node } = highlightedNode;
        let shouldHighLightItem = false;
        const { dynamicNodesByPopulation, dynamicEffectsByPopulation, dynamicDependenciesByPopulation } = prepareDynamicNodes(dynamics, variableNodes, id, color, y, highlightedNode, refinement, currentRefinementTab);
        dynamicNodes = [...dynamicNodes, ...dynamicNodesByPopulation];
        effectsOnVariable = [...effectsOnVariable, ...dynamicEffectsByPopulation];
        dynamicDependencies = [...dynamicDependencies, ...dynamicDependenciesByPopulation];
        if (node?.type === NodeType.DYNAMIC) {
            const isDynamicRelatedToPopulation = dynamics.some(({ id }) => id === node.id);
            shouldHighLightItem =
                dynamicEffects.some(({ dynamicId }) => dynamicId == node?.id) || isDynamicRelatedToPopulation;
        }
        if (node?.type === NodeType.VARIABLE) {
            const dynamicIds = dynamics.map(({ id }) => id);
            const isVariableRelatedToPopulation = ids.some((item) => dynamicIds.includes(item));
            shouldHighLightItem =
                dynamicEffects.some(({ dynamicId }) => ids.includes(dynamicId)) || isVariableRelatedToPopulation;
        }
        populationNodes.push({
            id: `p${id}`,
            originalId: id,
            name,
            internalType: NodeType.POPULATION,
            bottomYPos: populationBottomYPos,
            height: populationHeight,
            position: { x: 0, y },
            color: getElementColor({ color }, isCompleted, isHighlight, shouldHighLightItem),
            highlightColor: shouldHighLightItem ? undefined : DEFAULT_GREY,
            isNoDrivingPopulation: isNoDriving,
            isCompleted,
        });
    });
    return {
        populationNodes,
        dynamicNodes,
        effectsOnVariable,
        dynamicDependencies,
    };
};
export const isVariableUsedInDynamics = (variableId, dynamicId, populations) => {
    for (const population of populations) {
        const { dynamics } = population;
        for (const dynamic of dynamics) {
            if (dynamic.id === dynamicId) {
                const { dependencies, effectOnVariables } = dynamic;
                const hasDeps = dependencies.some((item) => item.variableId === variableId);
                const hasEffects = effectOnVariables.some((item) => item.variableId === variableId);
                if (hasDeps || hasEffects) {
                    return true;
                }
            }
        }
    }
    return false;
};
export const prepareVariableNodes = (variables, populations, highlightedNode) => {
    return variables.map(({ id, name, color, canHandleDynamicEffect, unit }, index) => {
        let shouldHighLightItem;
        const { node } = highlightedNode;
        if (node?.type === NodeType.VARIABLE) {
            shouldHighLightItem = id === highlightedNode.node?.id;
        }
        if (node?.type === NodeType.DYNAMIC) {
            shouldHighLightItem = isVariableUsedInDynamics(id, node.id, populations);
        }
        return {
            id: `v${id}`,
            originalId: id,
            name,
            unit,
            internalType: NodeType.VARIABLE,
            color: highlightedNode.isHighlight
                ? shouldHighLightItem
                    ? color
                    : DEFAULT_GREY
                : color || DEFAULT_COLOR,
            highlightColor: shouldHighLightItem ? HIGHLIGHT_COLOR : undefined,
            position: {
                x: 0,
                y: index * VARIABLE_NODE_HEIGHT,
            },
            canHandleDynamicEffect,
        };
    });
};
export const calcVariablesMinTotalHeight = (length) => (VARIABLE_NODE_HEIGHT + VARIABLE_TOP_MARGIN) * (length ?? 0);
export const calcPopulationsTotalHeight = (populationNodes, populationDialogOpen) => {
    let populationsTotalHeight = (populationNodes ?? [])?.reduce((acc, { height }) => acc + (height ?? 0) + POPULATION_BOTTOM_MARGIN, 0);
    if (populationDialogOpen) {
        populationsTotalHeight += 102;
    }
    return populationsTotalHeight + POPULATION_BOTTOM_MARGIN;
};
const calcVariableYPosition = (index, length, canvasHeight, editable) => {
    const variablesHeight = VARIABLE_TOP_MARGIN + (VARIABLE_NODE_HEIGHT + VARIABLE_BOTTOM_MIN_MARGIN) * length + ADD_BUTTON_HEIGHT;
    let canvasMinHeight = Math.max(canvasHeight, variablesHeight);
    if (editable) {
        canvasMinHeight -= ADD_BUTTON_HEIGHT;
    }
    let variableBottomMargin = canvasMinHeight - VARIABLE_TOP_MARGIN - VARIABLE_BOTTOM_MIN_MARGIN - VARIABLE_NODE_HEIGHT * length;
    if (length > 1) {
        variableBottomMargin /= length - 1;
    }
    return VARIABLE_TOP_MARGIN + VARIABLE_NODE_HEIGHT * index + variableBottomMargin * index;
};
export const refineVariableNode = (item, index, length, canvasHeight, widgetWidth, editable) => refineNode({
    ...item,
    position: {
        x: widgetWidth - VARIABLE_NODE_WIDTH,
        y: calcVariableYPosition(index, length, canvasHeight, editable),
    },
}, widgetWidth);
export const calcVariableDialogTopPosition = (variableNodes = [], canvasHeight) => {
    const index = variableNodes.length - 1;
    const item = variableNodes[index];
    if (item) {
        return (calcVariableYPosition(index, variableNodes.length, canvasHeight, true) +
            VARIABLE_NODE_HEIGHT +
            VARIABLE_TOP_MARGIN);
    }
    return VARIABLE_TOP_MARGIN;
};
export const getPopulationById = (populations, populationId) => populations.find((population) => population.id === populationId) || {};
const getDynamicById = (populations, dynamicId) => {
    for (const population of populations) {
        for (const dynamic of population.dynamics) {
            if (dynamic.id === dynamicId) {
                return dynamic;
            }
        }
    }
};
export const hasDynamicEffectsOnPopulation = (populations, dynamicId) => {
    for (const population of populations) {
        for (const effect of population.dynamicEffects) {
            if (effect.dynamicId === dynamicId) {
                return true;
            }
        }
    }
    return false;
};
export const hasVariableConnections = (populations, variableId) => {
    let hasDependency = false;
    let hasEffect = false;
    for (const population of populations) {
        for (const dynamic of population.dynamics) {
            if (!hasDependency) {
                for (const dependency of dynamic.dependencies) {
                    if (dependency.variableId === variableId) {
                        hasDependency = true;
                        break;
                    }
                }
            }
            if (!hasEffect) {
                for (const effect of dynamic.effectOnVariables) {
                    if (effect.variableId === variableId) {
                        hasEffect = true;
                        break;
                    }
                }
            }
        }
        if (hasDependency && hasEffect) {
            break;
        }
    }
    return { hasDependency, hasEffect };
};
export const getPopulationRemoveConfirmationWarning = (population) => {
    const { dynamicEffects = [], dynamics = [] } = population;
    const warnings = [];
    if (dynamics.length > 0) {
        warnings.push("all the Dynamics of this Population");
    }
    if (dynamicEffects.length > 0) {
        warnings.push("all the Effects that any Dynamic has on this Population");
    }
    return warnings.length > 0
        ? `If you delete this Population, you will also delete ${warnings.join(" and ")}.`
        : "";
};
export const getDynamicRemoveConfirmationWarning = (populations, dynamicId) => {
    const { dependencies = [], effectOnVariables = [] } = getDynamicById(populations, dynamicId) || {};
    const warnings = [];
    if (hasDynamicEffectsOnPopulation(populations, dynamicId)) {
        warnings.push("all its Effects on the Populations");
    }
    if (effectOnVariables.length > 0) {
        warnings.push("all its Effects on the Environment Variables");
    }
    if (dependencies.length > 0) {
        warnings.push("all its Dependencies on the Environment Variables");
    }
    const tail = warnings.pop();
    const allWarnings = warnings.length > 0 ? [warnings.join(", ")] : [];
    tail && allWarnings.push(tail);
    return allWarnings.length > 0
        ? `If you delete this Dynamic, you will also delete ${allWarnings.join(" and ")}.`
        : "";
};
export const getVariableRemoveConfirmationWarning = (populations, variableId) => {
    const { hasDependency, hasEffect } = hasVariableConnections(populations, variableId);
    const warnings = [];
    if (hasEffect) {
        warnings.push("all Effects on this Environment Variable");
    }
    if (hasDependency) {
        warnings.push("all Dependencies that any Dynamic has on this Environment Variable");
    }
    return warnings.length > 0
        ? `If you delete this Environment Variable, you will also delete ${warnings.join(" and ")}.`
        : "";
};
export const formatCellModelParameterValue = (parameterValue) => {
    const regexp = /\[\[(.+?)\]\]/g;
    let result = `${parameterValue}`;
    const matches = [...result.matchAll(regexp)];
    for (const match of matches) {
        const numberValue = Number(match[1]);
        result = result.replace(match[0], Number.isNaN(numberValue) ? match[1] : renderNumberValue(numberValue));
    }
    return result;
};
export const strippedUncertaintyProperties = (uncertaintyProperties) => {
    if (!uncertaintyProperties) {
        return null;
    }
    const { type, ...properties } = uncertaintyProperties;
    return Object.entries(properties).reduce((parsedDynamic, [dynamicKey, dynamicValue]) => ({
        ...parsedDynamic,
        [dynamicKey]: getNumberFromNumberString(String(dynamicValue), false),
    }), { type });
};
export const isUncertaintyPropertiesConfigured = (data) => {
    if (!data) {
        return false;
    }
    switch (data?.type) {
        case DynamicType.RANGE:
            return isNotEmpty(data.from, true) && isNotEmpty(data.to, true);
        case DynamicType.FIXED:
            return isNotEmpty(data.value, true);
        case DynamicType.DISTRIBUTION:
            return isNotEmpty(data.mean, true) && isNotEmpty(data.sd, true);
        default:
            return false;
    }
};
export const isDependencyDataConfigured = (data) => {
    switch (data?.type) {
        case DependencyType.Range:
            return isUncertaintyPropertiesConfigured(data.from) && isUncertaintyPropertiesConfigured(data.to);
        case DependencyType.OutOfRange:
            return isUncertaintyPropertiesConfigured(data.from) && isUncertaintyPropertiesConfigured(data.to);
        case DependencyType.Distribution:
            return isUncertaintyPropertiesConfigured(data.mean) && isUncertaintyPropertiesConfigured(data.sd);
        case DependencyType.AsymmetricalDistribution:
            return (isUncertaintyPropertiesConfigured(data.mean) &&
                isUncertaintyPropertiesConfigured(data.leftSD) &&
                isUncertaintyPropertiesConfigured(data.rightSD));
        case DependencyType.NegativeAsymmetricalDistribution:
            return (isUncertaintyPropertiesConfigured(data.mean) &&
                isUncertaintyPropertiesConfigured(data.leftSD) &&
                isUncertaintyPropertiesConfigured(data.rightSD));
        case DependencyType.NegativeDistribution:
            return isUncertaintyPropertiesConfigured(data.mean) && isUncertaintyPropertiesConfigured(data.sd);
        case DependencyType.Saturating:
            return isUncertaintyPropertiesConfigured(data.halfEffect);
        case DependencyType.NegativeSaturating:
            return isUncertaintyPropertiesConfigured(data.halfEffect);
        case DependencyType.Sigmoidal:
            return (isUncertaintyPropertiesConfigured(data.percent50) && isUncertaintyPropertiesConfigured(data.percent95));
        case DependencyType.NegativeSigmoidal:
            return (isUncertaintyPropertiesConfigured(data.percent05) && isUncertaintyPropertiesConfigured(data.percent50));
    }
    return false;
};
export const getCellModelParametersToOptimizeList = (cellModelParameters, usedParameterIds) => Object.keys(cellModelParameters).reduce((acc, groupKey) => {
    const parameters = cellModelParameters[groupKey]
        .filter(({ id }) => !usedParameterIds.includes(id))
        .map(({ name, id }) => ({
        label: name,
        value: { type: OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE, id },
    }));
    return [...acc, ...parameters];
}, []);
export const getSelectedCellModelParameterToOptimize = (target, parameterOverrides, parameterId) => {
    const { parameters } = { ...target?.state };
    if (!parameters) {
        return;
    }
    for (const groupKey of Object.keys(parameters)) {
        const parameter = parameters[groupKey].find((parameter) => parameter.id === parameterId);
        if (parameter) {
            const isParameterComplete = parameter.isComplete;
            let value = isParameterComplete ? formatCellModelParameterValue(parameter.parameterValue || "") : "";
            if (isParameterComplete && parameterOverrides) {
                const alteredParameter = parameterOverrides.find(({ type, id }) => parameter.id === id && type === GlobalEntityType.CellModel);
                if (alteredParameter) {
                    value = formatCellModelParameterValue(alteredParameter.parameterValue ?? "");
                }
            }
            return {
                type: OptimizationParameterParameterType.CELL_MODEL_OR_RECIPE,
                name: parameter.name ?? "",
                value,
                units: (parameter?.units === NoUnitsType.NoUnits
                    ? ""
                    : findDictionaryItemByValue(recipeParameterUnitsType, parameter?.units, getDefaultUnitsPrefix)
                        ?.label || ""),
            };
        }
    }
    return;
};
export const sortEdgesByHighlight = (edges) => edges.sort((a, b) => a.data.shouldHighLightEdge - b.data.shouldHighLightEdge);
export const checkRefinementIsCalculated = (status) => !!status && status === RefinementRunState.FINISHED;
export const checkRefinementIsInProgress = (status) => !!status && (status === RefinementRunState.CREATED || status === RefinementRunState.STARTED);
export const checkIsRefinementFailed = (status) => !!status &&
    [
        RefinementRunState.FAILED_TO_PAUSE,
        RefinementRunState.FAILED_TO_CANCEL,
        RefinementRunState.FAILED_TO_START,
        RefinementRunState.ERROR,
        RefinementRunState.MISSED,
    ].includes(status);
export const checkIsRefinementPaused = (status) => !!status && status === RefinementRunState.PAUSED;
export const checkIsRefinementCanceled = (status) => !!status && status === RefinementRunState.CANCELED;
export const handleShowRefinementNotifications = (payload) => {
    const startedRefinementId = localStorage.getItem(REFINEMENT_STARTED_LOCAL_STORAGE_ITEM);
    const removeStorageItem = () => localStorage.removeItem(REFINEMENT_STARTED_LOCAL_STORAGE_ITEM);
    if (payload.refined && payload.refinement?.status) {
        const withError = checkIsRefinementFailed(payload.refinement.status);
        const showErrorToast = () => Toaster().show({
            message: "Refinement has failed.",
            intent: Intent.DANGER,
            icon: toastIcons.error,
        });
        if (startedRefinementId === String(payload.cellModelId)) {
            if (withError) {
                showErrorToast();
            }
            else {
                Toaster().show({
                    message: "Refinement has started.",
                    intent: Intent.SUCCESS,
                    icon: toastIcons.info,
                    onDismiss: removeStorageItem,
                });
            }
        }
        else if (!payload.withoutToastError && withError) {
            showErrorToast();
        }
    }
    if (isNotEmpty(startedRefinementId)) {
        removeStorageItem();
    }
};
export const getRefineResultByParameterId = (parameterId) => (refinement, currentRefinementTab) => {
    if (!refinement || !parameterId || !isNotEmpty(currentRefinementTab)) {
        return;
    }
    const resultByParameterId = refinement.results.find((result) => result.parameterId === parameterId);
    return resultByParameterId?.results[currentRefinementTab] ?? undefined;
};
const getAccuracyValueFromRefinementAccuracyLevel = (accuracyLevel) => {
    switch (accuracyLevel) {
        case AccuracyLevel.LOW_ACCURACY:
            return Accuracy.LOW;
        case AccuracyLevel.MEDIUM_ACCURACY:
            return Accuracy.MEDIUM;
        case AccuracyLevel.HIGH_ACCURACY:
            return Accuracy.HIGH;
        default:
            return;
    }
};
export const getRefinementAccuracyIcon = (accuracyLevel) => (React.createElement(AccuracyIcon, { title: findDictionaryItemByValue(accuracyLevels, accuracyLevel)?.label, accuracy: getAccuracyValueFromRefinementAccuracyLevel(accuracyLevel) }));
export const prepareRefinementValidationErrors = (configurationResult) => {
    if (!configurationResult) {
        return;
    }
    const preparedProcessDataValidationResults = configurationResult.refinementProcessDataValidationResults.map((result) => ({
        ...result,
        ruleBreaks: result.ruleBreaks.reduce((acc, ruleBreak) => {
            const existedItemIdx = acc.findIndex(({ message, ruleBroken }) => message === ruleBreak.message && ruleBroken === ruleBreak.ruleBroken);
            if (existedItemIdx === -1) {
                return [...acc, ruleBreak];
            }
            const existedItem = acc[existedItemIdx];
            acc.splice(existedItemIdx, 1, {
                ...existedItem,
                invalidVariables: uniq([...existedItem.invalidVariables, ...ruleBreak.invalidVariables]),
            });
            return acc;
        }, []),
    }));
    return {
        ...configurationResult,
        refinementProcessDataValidationResults: preparedProcessDataValidationResults,
    };
};
